import React, { useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
  colors,
  IconButton,
  Collapse,
  Box,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  HelpRounded,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@material-ui/icons";
import { formatDate } from "../utils/dateUtils";
import { DateFormats } from "../utils/constants";
import { DataContext } from "../DataContext";
import { formatDecimal } from "../utils/functions";
import PopupMenuButton from "./PopupMenuButton";
import { ColorValues } from "../theme/colors";
import SpanForTooltip from "./SpanForTooltip";
import { useQuickEntryTableStyles } from "../theme/quickEntryTableStyles";
import NoDataRow from "./NoDataRow";
import LoadingRow from "./LoadingRow";
import clsx from "clsx";

const InvStatusBadge = ({ children }) => {
  if (!children) return null;
  let color = "";
  switch (children.toUpperCase()) {
    case "DRAFT":
      color = ColorValues.GREY;
      break;
    case "UNPAID":
      color = ColorValues.ORANGE;
      break;
    case "PAID":
      color = ColorValues.GREEN;
      break;
    case "WRITTEN OFF":
      color = ColorValues.RED;
      break;
    default:
      break;
  }
  const style = {
    display: "inline-block",
    borderRadius: "8px",
    padding: "4px 8px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "1px",
    textTransform: "uppercase",
    cursor: "default",
    whiteSpace: "nowrap",
    color: color && colors[color][500],
    backgroundColor: color && colors[color][500] + "25",
  };
  return (
    <span className="InvStatusBadge" style={style}>
      {children}
    </span>
  );
};

const useRowStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      verticalAlign: "middle",
    },
  },
  subInvTitle: {
    paddingTop: theme.spacing(1.25),
    fontWeight: 600,
  },
  wrapCell: {
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: theme.palette.background.default,
    opacity: 0.8,
  },
  hideBorderBottom: {
    "& > td": {
      borderBottomColor: "transparent !important",
    },
  },
  cellNoWrap: {
    whiteSpace: "nowrap",
  },
}));

const CollapsibleTableRow = ({
  invoice,
  getAllMenuItems,
  getMenuItemsForSubInvoice,
  hideCaseCol,
  hideOptionCol,
  hideStatusCol,
  hidePaymentDueCol,
  showClientCol,
  colSpan,
}) => {
  const classes = useQuickEntryTableStyles();
  const rowClasses = useRowStyles();
  const theme = useTheme();
  const { invoiceTypes, clientList, staffList } = useContext(DataContext);
  const [open, setOpen] = React.useState(false);

  const hasSubInvoices = Boolean(
    invoice.subInvoices && invoice.subInvoices.length > 0
  );
  return (
    <>
      <TableRow
        className={clsx(rowClasses.root, open && rowClasses.hideBorderBottom)}
        key={invoice.id}
        hover={invoice.active}
        style={{ opacity: invoice.active ? 1 : 0.7 }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={!hasSubInvoices}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          {invoice.invoiceNum === "-" ? (
            <Tooltip
              title="Invoice No. will be auto-generated once finalized"
              arrow
            >
              <SpanForTooltip>(Auto)</SpanForTooltip>
            </Tooltip>
          ) : (
            <span style={{ fontWeight: 600 }}>{invoice.invoiceNum}</span>
          )}
          {!invoice.active && (
            <Tooltip title="This is an archived item">
              <SpanForTooltip
                style={{
                  marginLeft: "6px",
                }}
              >
                <HelpRounded color="disabled" style={{ fontSize: "18px" }} />
              </SpanForTooltip>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{formatDate(invoice.date, DateFormats.MEDIUM)}</TableCell>
        {showClientCol && (
          <TableCell>
            {clientList.find((el) => el.id === invoice.clientSid)?.value}
          </TableCell>
        )}
        {!hideCaseCol && (
          <TableCell>
            <Tooltip arrow title={invoice.caseName}>
              <SpanForTooltip
                style={{
                  letterSpacing: "1px",
                  fontSize: "0.875rem",
                  color: theme.palette.secondary.main,
                }}
              >
                Ref#{invoice.caseRef}
              </SpanForTooltip>
            </Tooltip>
          </TableCell>
        )}
        <TableCell>
          {
            staffList.find((el) => el.id === invoice.originatingLawyerSid)
              ?.value
          }
        </TableCell>
        {!hideStatusCol && (
          <TableCell>
            {/* {client && client.paymentTerm
      ? getPaymentOverDue(client.paymentTerm.id, invoice)
      : getPaymentOverDue(invoice.paymentTermSid, invoice)} */}
            <InvStatusBadge>
              {invoice.isInDraft
                ? "Draft"
                : invoiceTypes.find(
                    (el) =>
                      el.id ===
                      (invoice.invoiceType instanceof Object
                        ? invoice.invoiceType.id
                        : invoice.invoiceType)
                  )?.value}
            </InvStatusBadge>
          </TableCell>
        )}
        <TableCell align="right" className="e360-number">
          {invoice.taxPercentage ? formatDecimal(invoice.taxPercentage) : "-"}
        </TableCell>
        <TableCell align="right" className="e360-number">
          {invoice.discountAmount ? formatDecimal(invoice.discountAmount) : "-"}
        </TableCell>
        <TableCell align="right" className="e360-number">
          {formatDecimal(invoice.totalAmount)}
        </TableCell>
        {!hidePaymentDueCol && invoice.paymentDueDays && (
          <TableCell align="right">
            {parseInt(invoice.paymentDueDays) >= 0 ? (
              invoice.paymentDueDays
            ) : (
              <span
                style={{
                  color: theme.palette.secondary.main,
                }}
              >
                {Math.abs(parseInt(invoice.paymentDueDays)) + " (Overdue)"}
              </span>
            )}
          </TableCell>
        )}
        {!hideOptionCol && (
          <TableCell align="right">
            <PopupMenuButton
              size="small"
              color="default"
              isIconButton
              menuItems={getAllMenuItems(invoice)}
            />
          </TableCell>
        )}
      </TableRow>
      {hasSubInvoices && (
        <TableRow className={clsx(!open && rowClasses.hideBorderBottom)}>
          <TableCell className={rowClasses.wrapCell} colSpan={colSpan + 1}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Typography
                  className={rowClasses.subInvTitle}
                  variant="body1"
                  gutterBottom
                >
                  List of Sub-invoices
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Sub-invoice No.</TableCell>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Attn To</TableCell>
                      <TableCell>Address</TableCell>
                      {!hideStatusCol && <TableCell>Status</TableCell>}
                      <TableCell align="right">Share (%)</TableCell>
                      <TableCell align="right">Amount ($)</TableCell>
                      {!hideOptionCol && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {invoice.subInvoices.map((subInv, indx) => (
                      <TableRow key={subInv.subInvoiceSid}>
                        <TableCell>
                          <span style={{ fontWeight: 600 }}>
                            {subInv.subInvoiceNumber}
                          </span>
                        </TableCell>
                        <TableCell>{subInv.companyName}</TableCell>
                        <TableCell className={rowClasses.cellNoWrap}>
                          {subInv.attentionTo}
                        </TableCell>
                        <TableCell className={rowClasses.cellNoWrap}>
                          <span>{subInv.address1}</span>
                          {subInv.address2 ? (
                            <>
                              <br /> <span>{subInv.address2}</span>
                            </>
                          ) : null}
                        </TableCell>
                        {!hideStatusCol && (
                          <TableCell>
                            <InvStatusBadge>
                              {
                                invoiceTypes.find(
                                  (el) => el.id === subInv.subInvoiceTypeSid
                                )?.value
                              }
                            </InvStatusBadge>
                          </TableCell>
                        )}
                        <TableCell align="right" className="e360-number">
                          {formatDecimal(subInv.percentage)}
                        </TableCell>
                        <TableCell align="right" className="e360-number">
                          {formatDecimal(
                            (+subInv.percentage * +invoice.totalAmount) / 100.0
                          )}
                        </TableCell>
                        {!hideOptionCol && (
                          <TableCell align="right">
                            <PopupMenuButton
                              size="small"
                              color="default"
                              isIconButton
                              menuItems={
                                getMenuItemsForSubInvoice &&
                                getMenuItemsForSubInvoice(invoice, indx)
                              }
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const InvoiceTable = ({
  isLoading,
  data,
  getAllMenuItems,
  getMenuItemsForSubInvoice,
  client,
  hideCaseCol,
  hideOptionCol,
  hideStatusCol,
  hidePaymentDueCol,
  showClientCol,
}) => {
  const classes = useQuickEntryTableStyles();

  const colSpan =
    10 -
    (hideCaseCol ? 1 : 0) -
    (hideOptionCol ? 1 : 0) -
    (hideStatusCol ? 1 : 0) -
    (hidePaymentDueCol ? 1 : 0) +
    (showClientCol ? 1 : 0);

  return (
    <TableContainer className="InvoiceTable">
      <Table>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell />
            <TableCell>Invoice No.</TableCell>
            <TableCell>Created On</TableCell>
            {showClientCol && <TableCell>Invoice To</TableCell>}
            {!hideCaseCol && <TableCell>Case</TableCell>}
            <TableCell>Originating Lawyer</TableCell>
            {!hideStatusCol && <TableCell>Status</TableCell>}
            <TableCell align="right">Tax (%)</TableCell>
            <TableCell align="right">Discount ($)</TableCell>
            <TableCell align="right">Amount ($)</TableCell>
            {!hidePaymentDueCol && (
              <TableCell align="right">Payment Due In (Days)</TableCell>
            )}
            {!hideOptionCol && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {isLoading ? (
            <LoadingRow colSpan={colSpan} />
          ) : data && data.length > 0 ? (
            data.map((invoice) => (
              <CollapsibleTableRow
                key={invoice.id}
                invoice={invoice}
                getAllMenuItems={getAllMenuItems}
                getMenuItemsForSubInvoice={getMenuItemsForSubInvoice}
                hideCaseCol={hideCaseCol}
                hideOptionCol={hideOptionCol}
                hideStatusCol={hideStatusCol}
                hidePaymentDueCol={hidePaymentDueCol}
                showClientCol={showClientCol}
                colSpan={colSpan}
              />
            ))
          ) : (
            <NoDataRow colSpan={colSpan} />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InvoiceTable;
